import { AsyncPipe } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	inject,
	OnInit,
	signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AlertService } from '@context/frontend/alert';
import {
	DialogBase,
	DialogOptions,
	DialogService,
} from '@context/frontend/common';
import {
	ContentService,
	SelectContentDialogComponent,
} from '@context/frontend/content';
import { applyValidators } from '@context/frontend/form';
import { PaginatedPageItem } from '@context/frontend/pagination';
import { StorageService } from '@context/frontend/storage';
import { DynamicEntityListComponent } from '@context/frontend/ui/components';
import { AuthService } from '@context/frontend/user';
import { Content } from '@context/shared/types/common';
import { ContentLog } from '@context/shared/types/log';
import {
	ContentMedia,
	ExistingFile,
	FileState,
} from '@context/shared/types/media';
import { User, UserOption } from '@context/shared/types/user';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiAutoFocus } from '@taiga-ui/cdk';
import {
	TuiButton,
	TuiError,
	TuiIcon,
	TuiLoader,
	TuiInput,
} from '@taiga-ui/core';
import {
	TuiButtonLoading,
	TuiFileLike,
	TuiFiles,
	TuiInputFiles,
	TuiTextarea,
} from '@taiga-ui/kit';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import {
	DocumentReference,
	DocumentSnapshot,
	getDoc,
} from 'firebase/firestore';
import { LogService } from '../../services';

export type LogCreationResult = {
	parentContentId: string;
	log: ContentLog;
};

export type LogFormDialogDismiss = ContentLog | LogCreationResult[] | null;

export type LogFormDialogData = {
	fileId?: string;
	content?: Content;
	contentRef?: DocumentReference<Content>;
	contentPreview?: boolean;
	log?: ContentLog;
	logRef?: DocumentReference<ContentLog>;
};

@Component({
	imports: [
		ReactiveFormsModule,
		TuiInput,
		TranslocoModule,
		TuiError,
		AsyncPipe,
		TuiTextarea,
		TuiButton,
		TuiButtonLoading,
		TuiFiles,
		TuiIcon,
		TuiInputFiles,
		TuiLoader,
		TuiAutoFocus,
		DynamicEntityListComponent,
	],
	selector: 'ctx-log-form-dialog',
	templateUrl: 'log-form-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogFormDialogComponent
	extends applyValidators(DialogBase<LogFormDialogDismiss, LogFormDialogData>)
	implements OnInit
{
	static override readonly DialogOptions = {
		label: 'log.create-log',
		size: 'm',
		dismissible: false,
	} as DialogOptions;

	private readonly authService = inject(AuthService);
	private readonly logService = inject(LogService);
	private readonly alert = inject(AlertService);
	private readonly storageService = inject(StorageService);
	private readonly contentService = inject(ContentService);
	private readonly dialog = inject(DialogService);
	private readonly cdRef = inject(ChangeDetectorRef);
	readonly existingFiles = signal<ExistingFile[]>([]);
	form: FormGroup | null = null;

	readonly busy = signal(false);
	readonly loading = signal(false);

	readonly selectedCrops = signal<DocumentReference<Content>[]>([]);
	readonly selectedCropData = signal<Map<string, Content>>(new Map());

	readonly cropListItems = computed(() => {
		const crops = this.selectedCropData();
		return this.selectedCrops()
			.map((ref) => {
				const crop = crops.get(ref.id);
				return crop
					? {
							id: ref.id,
							name: crop.name,
							ref,
							data: crop,
						}
					: null;
			})
			.filter((item): item is NonNullable<typeof item> => item !== null);
	});

	ngOnInit() {
		// Guard against a rejected setup (e.g. media blob fetch fails) leaving the
		// dialog stuck on its loading spinner forever.
		this.setupForm().catch((error) => {
			console.error('LogFormDialog: failed to set up form', error);
			this.loading.set(false);
			this.cdRef.markForCheck();
		});
	}

	readonly onSelectCrops = () =>
		this.dialog
			.open<PaginatedPageItem<Content>[]>(
				new PolymorpheusComponent(SelectContentDialogComponent),
				SelectContentDialogComponent.Options({
					data: {
						type: 'file',
						multiSelect: true,
						initialSelection: this.selectedCrops(),
					},
					label: 'log.crops',
				}),
			)
			.subscribe({
				next: (selected) => {
					if (!selected) return;

					const data = new Map<string, Content>();
					const refs = new Map<string, DocumentReference<Content>>();
					selected.forEach((item) => {
						data.set(item.ref.id, item.data);
						refs.set(item.ref.id, item.ref);
					});

					const selectedRefs = Array.from(refs.values());
					this.form?.patchValue({ crops: selectedRefs });

					this.selectedCrops.set(selectedRefs);
					this.selectedCropData.set(data);
					this.cdRef.markForCheck();
				},
			});

	readonly onRemoveCrop = (item: {
		id: string;
		ref: DocumentReference<Content>;
	}) => {
		const currentCrops = this.selectedCrops();
		const newCrops = currentCrops.filter((ref) => ref.id !== item.id);
		this.selectedCrops.set(newCrops);
		this.form?.patchValue({ crops: newCrops });

		const cropData = this.selectedCropData();
		cropData.delete(item.id);
		this.selectedCropData.set(new Map(cropData));
	};

	async setupForm() {
		this.loading.set(true);
		const defaultName = new Date().toLocaleDateString();
		const log = this.context.data.log;

		if (log) {
			this.form = new FormGroup({
				id: new FormControl(null),
				name: new FormControl(defaultName, (field) =>
					this.getRequiredValidator(
						field,
						'form.error.name.required',
					),
				),
				files: new FormControl([], (field) =>
					this.getMaxLengthValidator(field, 10),
				),
				thumbnails: new FormControl([]),
				description: new FormControl(null, (field) =>
					this.getRequiredValidator(
						field,
						'form.error.log-entry.required',
					),
				),
				createdBy: new FormControl(
					new UserOption(
						this.authService.user as User,
						this.authService.userRef as DocumentReference<User>,
					),
				),
			});

			const createdBySnapshot = await getDoc(
				log.createdBy as DocumentReference<User>,
			);

			const mediaRef = log.media;
			const media = await Promise.all(
				mediaRef.map((ref) =>
					getDoc(ref as DocumentReference<ContentMedia>).then(
						(doc) => doc.data() as ContentMedia,
					),
				),
			);

			this.form.patchValue({
				...this.context.data.log,
				createdBy: new UserOption(
					createdBySnapshot.data() as User,
					createdBySnapshot.ref as DocumentReference<User>,
				),
			});

			const files = await Promise.all(
				media.map((item) =>
					this.storageService
						.getBlob(
							this.storageService.getReference(item.storagePath),
						)
						.then((blob) =>
							Object.assign(
								new File([blob], item.name, {
									type: blob.type,
								}),
								{ state: 'active' as FileState, id: item.id },
							),
						),
				),
			);
			this.existingFiles.set(files);
		} else {
			this.form = new FormGroup({
				id: new FormControl(null),
				name: new FormControl(defaultName, (field) =>
					this.getRequiredValidator(
						field,
						'form.error.name.required',
					),
				),
				files: new FormControl([], (field) =>
					this.getMaxLengthValidator(field, 10),
				),
				thumbnails: new FormControl([]),
				description: new FormControl(null, (field) =>
					this.getRequiredValidator(
						field,
						'form.error.log-entry.required',
					),
				),
				createdBy: new FormControl(
					new UserOption(
						this.authService.user as User,
						this.authService.userRef as DocumentReference<User>,
					),
				),
				crops: new FormControl<DocumentReference<Content>[]>(
					[],
					(field) => {
						const v = field.value as
							DocumentReference<Content>[] | null;
						if (v?.length) return null;
						return {
							required: this.transloco.translate(
								'form.error.crops.required',
							),
						};
					},
				),
			});

			await this.applyCreatePrefills();
		}

		this.loading.set(false);
		this.form.get('createdBy')?.disable();
		this.cdRef.markForCheck();
	}

	private async applyCreatePrefills() {
		const { fileId, content, contentRef } = this.context.data;

		try {
			if (fileId) {
				const snapshot = await this.contentService.findById(fileId);
				this.mergeCropFromSnapshot(snapshot);
			} else if (contentRef && content) {
				this.mergeCropRefAndData(contentRef, content);
			} else if (contentRef) {
				const snapshot = await getDoc(contentRef);
				if (snapshot.exists()) {
					this.mergeCropFromSnapshot(snapshot);
				}
			}
		} catch (error) {
			console.error('Error prefilling log crops', error);
		}

		this.cdRef.markForCheck();
	}

	private mergeCropFromSnapshot(snapshot: DocumentSnapshot) {
		const ref = snapshot.ref as DocumentReference<Content>;
		const cropData = snapshot.data() as Content;
		this.mergeCropRefAndData(ref, cropData);
	}

	private mergeCropRefAndData(
		ref: DocumentReference<Content>,
		data: Content,
	) {
		const existing = (this.form?.get('crops')?.value ??
			[]) as DocumentReference<Content>[];
		if (existing.some((r) => r.id === ref.id)) return;

		const refs = [...existing, ref];
		const map = new Map(this.selectedCropData());
		map.set(ref.id, data);
		this.selectedCropData.set(map);
		this.selectedCrops.set(refs);
		this.form?.patchValue({ crops: refs });
	}

	onSubmit() {
		this.form?.markAllAsTouched();
		if (this.busy() || !this.form || this.form.invalid) return;

		this.busy.set(true);
		const value = this.form.getRawValue();
		this.form.disable();

		const createdByValue = value.createdBy as UserOption;
		value.createdBy = createdByValue.valueOf();

		if (this.context.data.log) {
			const removedFiles =
				this.existingFiles().filter((f) => f.state === 'deleted') ?? [];

			value.removedMedia = [
				...this.context.data.log.media.filter((m) =>
					removedFiles.some((f) => f.id === m.id),
				),
			];

			value.media = [
				...this.context.data.log.media.filter(
					(m) => !removedFiles.some((f) => f.id === m.id),
				),
			];

			const contentRef = this.context.data.contentRef;
			if (!contentRef) {
				this.busy.set(false);
				this.form?.enable();
				this.form?.get('createdBy')?.disable();
				return;
			}

			return this.logService
				.update(
					contentRef,
					value,
					this.context.data.logRef as DocumentReference<ContentLog>,
					{ addActivityLog: true, type: 'update' },
				)
				.then((log) => {
					this.onDismiss(log as ContentLog);
					this.alert.open('generic-success').subscribe();
				})
				.catch((error) => {
					console.error('There was an issue editing the log', error);
					this.alert.open('generic-error').subscribe();
				})
				.finally(() => {
					this.busy.set(false);
					this.form?.enable();
					this.form?.get('createdBy')?.disable();
				});
		}

		const cropRefs = [...(value.crops as DocumentReference<Content>[])];
		delete value.crops;

		return this.logService
			.createForCrops(cropRefs, value)
			.then((results) => {
				this.onDismiss(results as LogCreationResult[]);
				this.alert.open('generic-success').subscribe();
			})
			.catch((error) => {
				console.error('There was an issue creating the log', error);
				this.alert.open('generic-error').subscribe();
			})
			.finally(() => {
				this.busy.set(false);
				this.form?.enable();
				this.form?.get('createdBy')?.disable();
			});
	}

	onFileRemove(file: TuiFileLike) {
		const control = this.form?.get('files');
		if (!control) return;

		control.setValue(
			control.value.filter((current: TuiFileLike) => current !== file) ??
				[],
		);
	}

	readonly onExistingFileRemove = (file: ExistingFile) =>
		this.existingFiles().map((existing) => {
			if (existing === file) file.state = 'deleted';
			return file;
		});

	readonly onRemovedFileRestore = (file: ExistingFile) =>
		this.existingFiles().map((existing) => {
			if (existing === file) file.state = 'active';
			return file;
		});
}
